import {
  RevActionsHeader,
  RevButton,
  RevInput,
  RevLink,
  RevSpinner,
  RevTable,
  RevTooltip,
  type RevTablePaginationType,
  RevIconButton,
} from "@revmatics/catalyst";
import {
  faArrowLeft,
  faMagnifyingGlass,
} from "@fortawesome/pro-light-svg-icons";
import { useDebounce } from "../../helpers/hooks/useDebounce.tsx";
import { Fragment, useEffect, useMemo, useState } from "react";
import { CompanyItemDataType } from "../../stores/types/companyTypes.tsx";
import { useAppDispatch, useAppSelector } from "../../stores/hooks/hooks.ts";
import {
  ColumnDef,
  ColumnPinningState,
  createColumnHelper,
} from "@tanstack/react-table";
import { formatDateToMonthDayYear } from "../../helpers/functions/formatDate.tsx";
import { Menu, MenuButton, MenuItem, MenuItems } from "@headlessui/react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEllipsisVertical } from "@fortawesome/pro-solid-svg-icons";
import { Link, useNavigate } from "react-router";
import { AddCompanyDialog } from "./components/AddCompanyDialog.tsx";
import { fetchCompaniesList } from "../../stores/thunks/companyThunk.tsx";

const companyColumnHelper = createColumnHelper<CompanyItemDataType>();
const COMPANIES_SAVED_TABLE = "companiesTable";

export interface PaginationWithSearchType extends RevTablePaginationType {
  search: string;
}
export const CompaniesPage = () => {
  const saved: PaginationWithSearchType = JSON.parse(
    sessionStorage.getItem(COMPANIES_SAVED_TABLE) || "{}",
  );
  const navigate = useNavigate();
  const { companiesList, isLoadingCompaniesList } = useAppSelector(
    (state) => state.company,
  );
  const dispatch = useAppDispatch();
  const [searchValue, setSearchValue] = useState(saved.search || "");
  const debouncedSearch = useDebounce(searchValue, 500);
  const [isOpenAddCompany, setOpenAddCompany] = useState(false);
  const [columnPinning, setColumnPinning] = useState<ColumnPinningState>({
    right: ["actions"],
  });

  useEffect(() => {
    if (!companiesList) {
      dispatch(fetchCompaniesList());
    }
  }, [dispatch, companiesList]);

  useEffect(() => {
    sessionStorage.setItem(
      COMPANIES_SAVED_TABLE,
      JSON.stringify({
        ...saved,
        search: searchValue,
      }),
    );
  }, [saved, searchValue]);

  const displayCompanies = useMemo(() => {
    if (companiesList && companiesList.length > 0) {
      return companiesList.filter((item) =>
        item.name?.toLowerCase().includes(debouncedSearch.toLowerCase()),
      );
    } else {
      return null;
    }
  }, [companiesList, debouncedSearch]);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const columns = useMemo<ColumnDef<CompanyItemDataType, any>[]>(
    () => [
      companyColumnHelper.accessor("name", {
        id: "companyName",
        header: () => (
          <span className="whitespace-nowrap typography-body-bold-xs text-monochrome-900">
            Company Name
          </span>
        ),
        cell: (info) => {
          const originalData = info.row.original;
          return (
            <RevLink
              pathname={`/companies/${originalData.id}/`}
              text={originalData.name}
              className="w-fit"
            />
          );
        },
        enableSorting: true,
        size: 300,
      }),
      companyColumnHelper.accessor("id", {
        id: "id",
        header: () => (
          <span className="whitespace-nowrap typography-body-bold-xs text-monochrome-900">
            Company ID
          </span>
        ),
        cell: (info) => {
          const value = info.getValue();
          return (
            <span className="whitespace-nowrap typography-body-sm text-monochrome-900">
              {value}
            </span>
          );
        },
        enableSorting: true,
        size: 180,
      }),
      companyColumnHelper.accessor("employees", {
        id: "users",
        header: () => (
          <span className="whitespace-nowrap typography-body-bold-xs text-monochrome-900">
            Users
          </span>
        ),
        cell: (info) => {
          const value = info.getValue();
          return (
            <span className="whitespace-nowrap typography-body-sm text-monochrome-900">
              {value} User{value > 1 ? "s" : ""}
            </span>
          );
        },
        enableSorting: true,
        size: 180,
      }),
      companyColumnHelper.accessor("brands", {
        id: "brands",
        header: () => (
          <span className="whitespace-nowrap typography-body-bold-xs text-monochrome-900">
            Brands
          </span>
        ),
        cell: (info) => {
          const value = info.getValue();
          return (
            <span className="whitespace-nowrap typography-body-sm text-monochrome-900">
              {value} Brand{value > 1 ? "s" : ""}
            </span>
          );
        },
        enableSorting: true,
        size: 180,
      }),
      companyColumnHelper.accessor("created_at", {
        id: "createdOn",
        header: () => (
          <span className="whitespace-nowrap typography-body-bold-xs text-monochrome-900">
            Created on
          </span>
        ),
        cell: (info) => {
          const value = info.getValue();
          return (
            <span className="whitespace-nowrap typography-body-sm text-monochrome-900">
              {formatDateToMonthDayYear(value)}
            </span>
          );
        },
        enableSorting: true,
        size: 180,
      }),

      companyColumnHelper.display({
        id: "actions",
        header: () => <span></span>,
        cell: (info) => {
          const originalData = info.row.original;
          return (
            <div className="flex items-center gap-1 space-x-1 justify-center">
              <Menu>
                {({ open }) => {
                  // Define the trigger button as a variable to avoid repeating the JSX
                  const TheMenuButton = (
                    <MenuButton className="inline-flex items-center gap-2 rounded-md px-3 py-1.5 text-sm/6 font-semibold text-monochrome-500 shadow-inner shadow-white/10 focus:outline-none data-hover:bg-monochrome-100">
                      <FontAwesomeIcon icon={faEllipsisVertical} />
                    </MenuButton>
                  );

                  return (
                    <div className="relative">
                      {!open ? (
                        <RevTooltip content="More Actions" position="left">
                          {TheMenuButton}
                        </RevTooltip>
                      ) : (
                        TheMenuButton
                      )}

                      <MenuItems
                        anchor={{ to: "bottom end", gap: 4 }}
                        transition
                        className="origin-top transition duration-200 outline-0 ease-out shadow-elevation-2 rounded-lg bg-white py-2 z-10 flex flex-col w-[200px]"
                      >
                        <MenuItem as={Fragment}>
                          <Link
                            to={`/companies/${originalData.id}/`}
                            className="w-full typography-body-sm text-start text-monochrome-900 py-2 px-3 disabled:text-monochrome-400 hover:bg-monochrome-50"
                          >
                            View / Edit Company
                          </Link>
                        </MenuItem>
                      </MenuItems>
                    </div>
                  );
                }}
              </Menu>
            </div>
          );
        },
        size: 48,
      }),
    ],
    [],
  );

  if (isLoadingCompaniesList) {
    return (
      <div className="h-full p-4 sm:p-6 lg:p-8 text-center">
        <RevSpinner size="large" color="primary" centered />
      </div>
    );
  }

  return (
    <div>
      <RevActionsHeader
        title="Companies"
        subtitle="Here you can find and manage information about companies, businesses and their users."
        subtitleClassName="typography-body-sm text-monochrome-600"
        headerClassName="!mb-0"
        backButton={
          <RevIconButton
            handleClick={() => navigate(-1)}
            icon={faArrowLeft}
            size="xl"
            variant="outline"
            iconSize="medium"
            aria-label="Back button brand details"
            className="bg-white"
          />
        }
      >
        <RevButton
          text="Add Company"
          buttonSize="medium"
          handleClick={() => setOpenAddCompany(true)}
          className="whitespace-nowrap w-full sm:w-fit"
        />
      </RevActionsHeader>
      <div className="w-full md:w-[555px] mt-4 mb-5">
        <RevInput
          name="search"
          type="text"
          label=""
          size="md"
          placeholder="Search"
          enableClearButton
          onClear={() => setSearchValue("")}
          iconLeft={faMagnifyingGlass}
          onChange={(e) => setSearchValue(e.target.value)}
          value={searchValue}
          className="w-fit"
        />
      </div>
      <RevTable<CompanyItemDataType>
        data={displayCompanies || []}
        columns={columns}
        initialPageSize={saved.pageSize || 10}
        initialPageIndex={saved.pageIndex || 0}
        enableGlobalFilter={false}
        columnPinning={columnPinning}
        onColumnPinningChange={setColumnPinning}
        paginationSaved={COMPANIES_SAVED_TABLE}
        className="pb-6"
      />
      <AddCompanyDialog
        isOpenDialog={isOpenAddCompany}
        setOpenDialog={setOpenAddCompany}
      />
    </div>
  );
};
